import React, { useState, useEffect } from 'react';
import { Conversation, Message } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, Send, Paperclip, CheckCheck, Clock, User as UserIcon } from 'lucide-react';

export const BuyerMessages: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchConversations = async () => {
    try {
      const res = await fetch('/api/conversations');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.conversations) {
          setConversations(data.conversations);
          if (!activeConvId && data.conversations.length > 0) {
            setActiveConvId(data.conversations[0].id);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMessages = async (convId: string) => {
    try {
      const res = await fetch(`/api/conversations/${convId}/messages`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setMessages(data.messages || []);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (activeConvId) {
      fetchMessages(activeConvId);
      const interval = setInterval(() => fetchMessages(activeConvId), 4000); // Polling every 4s
      return () => clearInterval(interval);
    }
  }, [activeConvId]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeConvId || !newMessage.trim()) return;

    setSending(true);
    try {
      const res = await fetch(`/api/conversations/${activeConvId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage.trim() })
      });
      if (res.ok) {
        setNewMessage('');
        await fetchMessages(activeConvId);
        await fetchConversations();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-brand-lightBorder dark:border-brand-darkBorder pb-6 mb-6">
        <h1 className="text-3xl font-black text-brand-lightText dark:text-brand-darkText flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-brand-blue dark:text-brand-accent" />
          <span>Conversations & Order Messaging</span>
        </h1>
        <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
          Keep all communications inside GameVault to preserve 72-Hour Buyer Protection eligibility
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px] rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-xl overflow-hidden">
        
        {/* Left: Conversation List */}
        <div className="border-r border-brand-lightBorder dark:border-brand-darkBorder overflow-y-auto">
          <div className="p-4 border-b border-brand-lightBorder dark:border-brand-darkBorder">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-lightSecText">Active Chats</span>
          </div>

          {conversations.length > 0 ? (
            <div className="divide-y divide-brand-lightBorder dark:divide-brand-darkBorder">
              {conversations.map((c) => {
                const partnerName = c.participant_one_id === user?.id ? c.participant_two_name : c.participant_one_name;
                const isSelected = activeConvId === c.id;

                return (
                  <button
                    key={c.id}
                    onClick={() => setActiveConvId(c.id)}
                    className={`w-full p-4 text-left transition-colors flex items-start gap-3 ${
                      isSelected
                        ? 'bg-brand-blue/10 dark:bg-brand-blue/20'
                        : 'hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-brand-blue/20 text-brand-blue font-bold flex items-center justify-center flex-shrink-0">
                      {partnerName?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs text-brand-lightText dark:text-brand-darkText truncate">
                          {partnerName || 'Merchant'}
                        </span>
                        {c.unread_count ? (
                          <span className="w-2 h-2 rounded-full bg-brand-blue" />
                        ) : null}
                      </div>
                      <p className="text-[11px] text-brand-lightSecText dark:text-brand-darkSecText truncate mt-0.5">
                        {c.last_message_content || 'Order conversation created'}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-xs text-brand-lightSecText">
              No active conversations. Purchase an offer to chat with a verified seller.
            </div>
          )}
        </div>

        {/* Right: Message Stream & Input */}
        <div className="md:col-span-2 flex flex-col justify-between h-full bg-brand-lightSec/30 dark:bg-brand-darkBg/30">
          {activeConvId ? (
            <>
              {/* Message History */}
              <div className="p-6 overflow-y-auto space-y-4 flex-1">
                {messages.map((m) => {
                  const isMine = m.sender_id === user?.id;

                  return (
                    <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-md p-4 rounded-2xl text-xs space-y-1 ${
                        isMine
                          ? 'bg-brand-blue text-white rounded-br-none shadow-sm'
                          : 'bg-white dark:bg-brand-darkCard text-brand-lightText dark:text-brand-darkText border border-brand-lightBorder dark:border-brand-darkBorder rounded-bl-none shadow-sm'
                      }`}>
                        <div className="flex items-center justify-between gap-4 text-[10px] opacity-75">
                          <span>{m.sender_username || (isMine ? 'You' : 'Partner')}</span>
                          <span>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="whitespace-pre-line leading-relaxed">{m.content}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message Composer */}
              <form onSubmit={handleSendMessage} className="p-4 bg-white dark:bg-brand-darkCard border-t border-brand-lightBorder dark:border-brand-darkBorder flex gap-2">
                <input
                  type="text"
                  placeholder="Type a safe message to your seller..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center gap-1.5 transition-all disabled:opacity-40"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Send</span>
                </button>
              </form>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-xs text-brand-lightSecText">
              Select a conversation to start messaging
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
